import React, { useRef, useState, useEffect } from 'react';
import { SendHorizontal } from 'lucide-react';
import { Button, Textarea, Paper, Stack, Text, Avatar, Group, ScrollArea } from '@mantine/core';
import { Uuid, UuidString } from '~src/utils/UUID';
import { CommentMention } from '~/src/api/peep/comments';
import { peep_client } from '~/src/api/peep';
import { GroupMember } from '~/src/types/groups';

interface Props {
  placeholder: string;
  maxHeight: number;
  groupId: UuidString;
  onSubmit: (text: string, mentions: CommentMention[]) => void;
}

export default function CommentInput(props: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [commentText, setCommentText] = useState<string>('');
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);

  // Mention state
  const [isMentioning, setIsMentioning] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(-1); // Index of the '@' triggering the mention
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  // Local cache of alias -> userId to resolve mentions on submit
  // We use this to map the final text back to IDs.
  const [mentionMap, setMentionMap] = useState<Map<string, string>>(new Map());

  const [currentUserId, setCurrentUserId] = useState<UuidString | null>(null);

  useEffect(() => {
    // Fetch group members and current user on mount
    const fetchData = async () => {
      try {
        const [members, whoami] = await Promise.all([
          peep_client.groups.getGroupMembers(props.groupId),
          peep_client.users.whoami(),
        ]);
        setGroupMembers(members);
        setCurrentUserId(whoami);
      } catch (e) {
        console.error('Failed to fetch data', e);
      }
    };
    fetchData();
  }, [props.groupId]);

  const filteredMembers = groupMembers.filter(
    (m) =>
      m.user_id.toString() !== currentUserId?.toString() && m.alias.toLowerCase().includes(mentionQuery.toLowerCase()),
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newVal = e.target.value;
    const newCursorPos = e.target.selectionStart;
    setCommentText(newVal);

    // Sync scroll
    if (backdropRef.current) {
      backdropRef.current.scrollTop = e.target.scrollTop;
    }

    // Simple mention detection logic
    // specific condition: cursor is right after an '@' or inside a word starting with '@'
    const textBeforeCursor = newVal.slice(0, newCursorPos);
    const lastAtPos = textBeforeCursor.lastIndexOf('@');

    if (lastAtPos !== -1) {
      // Check if there is a space between @ and cursor (invalid unless it's the query)
      // Actually normally mentions allow spaces if we support "First Last", but here alias is single string?
      // Let's assume alias might have spaces, but we usually stop detecting if there is a newline or something.
      // For simplicity, let's strictly look for @Word
      const query = textBeforeCursor.slice(lastAtPos + 1);

      // If there is a whitespace BEFORE the @, or it operates at start of line, it's valid.
      // If detection was already active, or we just typed @
      const isStartOfLine = lastAtPos === 0;
      const isPrecededBySpace = lastAtPos > 0 && /\s/.test(newVal[lastAtPos - 1]);

      if (isStartOfLine || isPrecededBySpace) {
        setIsMentioning(true);
        setMentionIndex(lastAtPos);
        setMentionQuery(query);
        setSelectedMentionIndex(0);
        return; // maintain mentioning state
      }
    }

    setIsMentioning(false);
  };

  const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    if (backdropRef.current) {
      backdropRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  const insertMention = (member: GroupMember) => {
    if (mentionIndex === -1) return;

    const beforeMention = commentText.slice(0, mentionIndex);

    const cursor = inputRef.current?.selectionStart || mentionIndex + 1 + mentionQuery.length;
    const afterCursor = commentText.slice(cursor);

    const newText = beforeMention + '@' + member.alias + ' ' + afterCursor;

    setCommentText(newText);

    // Update map
    const newMap = new Map(mentionMap);
    newMap.set('@' + member.alias, member.user_id.toString());
    setMentionMap(newMap);

    setIsMentioning(false);

    // Focus back and set cursor
    setTimeout(() => {
      const newCursor = mentionIndex + 1 + member.alias.length + 1;
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCursor, newCursor);
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Atomic backspace for mentions
    if (e.key === 'Backspace') {
      const cursor = inputRef.current?.selectionStart ?? 0;
      const end = inputRef.current?.selectionEnd ?? 0;

      // Only handle single cursor deletion (no selection)
      if (cursor === end && cursor > 0) {
        // Check if text preceding cursor matches a known mention
        // We iterate through entries. The keys are '@Alias'.
        let matchKey: string | null = null;

        for (const key of mentionMap.keys()) {
          const startPos = cursor - key.length;
          if (startPos >= 0) {
            const slice = commentText.slice(startPos, cursor);
            if (slice === key) {
              matchKey = key;
              break;
            }
          }
        }

        if (matchKey) {
          e.preventDefault();
          const startPos = cursor - matchKey.length;
          const newText = commentText.slice(0, startPos) + commentText.slice(cursor);
          setCommentText(newText);

          // Remove from map
          const newMap = new Map(mentionMap);
          newMap.delete(matchKey);
          setMentionMap(newMap);

          setIsMentioning(false);

          // Restore cursor
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.setSelectionRange(startPos, startPos);
            }
          }, 0);
          return; // Skip normal backspace
        }
      }
    }

    if (!isMentioning) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex((i) => (i + 1) % filteredMembers.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex((i) => (i - 1 + filteredMembers.length) % filteredMembers.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (filteredMembers.length > 0) {
        insertMention(filteredMembers[selectedMentionIndex]);
      } else {
        setIsMentioning(false); // Close if no match and enter pressed
      }
    } else if (e.key === 'Escape') {
      setIsMentioning(false);
    }
  };

  const handleSendClick = () => {
    if (commentText) {
      // 1. Identify all mentions in the current text
      const rawMentions: { start: number; end: number; userId: string }[] = [];

      mentionMap.forEach((userId, aliasKey) => {
        let pos = commentText.indexOf(aliasKey);
        while (pos !== -1) {
          rawMentions.push({
            start: pos,
            end: pos + aliasKey.length,
            userId: userId,
          });
          pos = commentText.indexOf(aliasKey, pos + 1);
        }
      });

      // Sort by position to process sequentially
      rawMentions.sort((a, b) => a.start - b.start);

      // 2. Build clean text (stripping mentions) and new mention list
      let cleanText = '';
      const finalMentions: CommentMention[] = [];
      let lastIndex = 0;

      rawMentions.forEach((m) => {
        // Append text before mention
        if (m.start > lastIndex) {
          cleanText += commentText.slice(lastIndex, m.start);
        }

        // Record the insertion point in the clean text
        // This is simply the valid length of cleanText so far
        const insertionIndex = cleanText.length;

        finalMentions.push({
          mentioned_user_id: Uuid.from_string(m.userId),
          character_index: insertionIndex,
        });

        // Skip the mention text in the output string
        lastIndex = m.end;
      });

      // Append remaining text
      if (lastIndex < commentText.length) {
        cleanText += commentText.slice(lastIndex);
      }

      props.onSubmit(cleanText, finalMentions);
      setCommentText('');
      setMentionMap(new Map());
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const renderHighlights = () => {
    if (!commentText) return null;

    // Find all ranges to highlight
    const ranges: { start: number; end: number; key: string }[] = [];
    mentionMap.forEach((_, key) => {
      let pos = commentText.indexOf(key);
      while (pos !== -1) {
        ranges.push({ start: pos, end: pos + key.length, key });
        pos = commentText.indexOf(key, pos + 1);
      }
    });

    ranges.sort((a, b) => a.start - b.start);

    const nodes = [];
    let lastIndex = 0;

    ranges.forEach((range, i) => {
      if (range.start > lastIndex) {
        nodes.push(commentText.slice(lastIndex, range.start));
      }
      if (range.start >= lastIndex) {
        // ensure no overlap processing
        nodes.push(
          <span key={i} style={{ backgroundColor: '#eebefa', borderRadius: '3px' }}>
            {commentText.slice(range.start, range.end)}
          </span>,
        );
        lastIndex = range.end;
      }
    });

    if (lastIndex < commentText.length) {
      nodes.push(commentText.slice(lastIndex));
    }

    // Add a trailing space / break to ensure empty line rendering if needed
    if (commentText.endsWith('\n')) {
      nodes.push(<br key="last-br" />);
    }

    return nodes;
  };

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {isMentioning && filteredMembers.length > 0 && (
        <Paper
          shadow="md"
          radius="md"
          withBorder
          style={{
            position: 'absolute',
            bottom: '100%', // Open upwards
            left: 0,
            zIndex: 1000,
            width: '250px',
            marginBottom: '8px',
          }}
        >
          <ScrollArea.Autosize mah={200}>
            <Stack gap={0}>
              {filteredMembers.map((member, index) => (
                <Group
                  key={member.user_id.toString()}
                  p="xs"
                  onClick={() => insertMention(member)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: index === selectedMentionIndex ? 'var(--mantine-color-gray-1)' : 'transparent',
                  }}
                  onMouseEnter={() => setSelectedMentionIndex(index)}
                  gap="sm"
                >
                  <Avatar src={member.profile_image} size="sm" radius="xl">
                    {member.alias.slice(0, 2).toUpperCase()}
                  </Avatar>
                  <Text size="sm">{member.alias}</Text>
                </Group>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Paper>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', width: '100%' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          {/* Backdrop for highlights */}
          <div
            ref={backdropRef}
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              padding: '8px 11px', // Exact match with Input
              pointerEvents: 'none',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              overflow: 'hidden',
              color: '#333',
              fontFamily: 'Lato, sans-serif',
              fontSize: '14px',
              lineHeight: '1.55',
              zIndex: 0,
              borderRadius: '4px', // Standard Mantine radius approx
            }}
          >
            {renderHighlights()}
          </div>

          <Textarea
            placeholder={props.placeholder}
            autosize
            minRows={1}
            maxRows={4}
            styles={{
              input: {
                backgroundColor: 'transparent',
                color: 'transparent',
                caretColor: 'black',
                padding: '8px 11px', // Exact match with Backdrop
                fontFamily: 'Lato, sans-serif',
                fontSize: '14px',
                lineHeight: '1.55',
                zIndex: 1,
                position: 'relative',
              },
            }}
            onChange={handleInputChange}
            onScroll={handleScroll}
            onKeyDown={handleKeyDown}
            value={commentText}
            ref={inputRef}
          />
        </div>
        <Button
          variant="filled"
          color="var(--global-main-lighter-8)"
          size="sm"
          radius="xl"
          disabled={!commentText}
          onClick={handleSendClick}
          style={{ zIndex: 2, marginBottom: '2px' }}
        >
          <SendHorizontal strokeWidth={2.5} size={18} color="var(--global-main)" />
        </Button>
      </div>
    </div>
  );
}
